// Orders Service - Service layer for order-related API operations

import axiosInstance from "../instance/axiosInstance";
import { ORDERS_ENDPOINTS } from "../endpoints/orders.endpoints";
import { serializeParams } from "../utils/paramSerializer";
import { handleApiError } from "../utils/errorHandler";
import { PAGINATION_DEFAULTS } from "../config/api.config";

// Helper to get store dynamically to avoid circular dependency
const getStore = () => require("../../store/store").default;

//const TEMP_ORDERS_EMAIL = "Sonia.Subhashsingh.Rajput@gds.ey.com";
const ORDERS_TEXT_PLAIN_ACCEPT_HEADER = { Accept: "text/plain" };

export const OrdersService = {
  // Get all orders with optional filtering and pagination
  getOrders: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || PAGINATION_DEFAULTS.page,
        limit: Math.min(
          params.limit || PAGINATION_DEFAULTS.limit,
          PAGINATION_DEFAULTS.maxLimit,
        ),
        ...(params.status && { status: params.status }),
        ...(params.search && { search: params.search }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
      };

      const response = await axiosInstance.get(ORDERS_ENDPOINTS.GET_ALL, {
        params: queryParams,
        paramsSerializer: serializeParams,
        headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER,
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get orders for the authenticated user (my orders)
  getMyOrders: async (params = {}) => {
    
    try {
      const page = params.page || PAGINATION_DEFAULTS.page;
      const pageSize =
        params.pageSize || params.limit || PAGINATION_DEFAULTS.limit;
      const roleId = params.roleId || "";
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_MY_ORDERS(page, pageSize, roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  //Get orders by type for the authenticated user (my orders)
  getOrdersByType: async (type, params = {}) => {
    if (!type) {
      throw new Error("Order type is required");
    }
    try {
      const roleId = params.roleId || "";
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ORDERS_BY_TYPE(type, roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get AI-allowed order IDs for a role
  getAIOrders: async (roleId) => {
    if (!roleId) {
      throw new Error("Role ID is required");
    }
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_AI_ORDERS(roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get a single order by ID
  getOrderById: async (orderId, roleId = "") => {
    if (!orderId) {
      throw new Error("Order ID is required");
    }

    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_BY_ID(orderId, roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get recently viewed orders for dropdown list
  getRecentlyViewedOrders: async (params = {}) => {
    try {
      const page = params.page || PAGINATION_DEFAULTS.page;
      const pageSize =
        params.pageSize || params.limit || PAGINATION_DEFAULTS.limit;
      const roleId = params.roleId || "";

      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_RECENTLY_VIEWED(page, pageSize, roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  //Update Order
  updateOrder: async (orderData) => {
    if (!orderData) {
      throw new Error("Order ID is required");
    }
    let response;
    try {
      response = await axiosInstance.post(ORDERS_ENDPOINTS.UPDATE, orderData, {
        headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER,
      });
    } catch (error) {
      throw handleApiError(error);
    }

    if (response?.data?.success === false) {
      throw handleApiError({
        response: {
          status: response.status >= 400 ? response.status : 400,
          data: response.data,
        },
      });
    }

    return response.data;
  },

  //get action required logs for an order
  getActionRequiredLogs: async (orderId) => {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ACTION_REQUIRED_LOGS(orderId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get open orders summaries for an order (used when opening from Open Orders section)
  getOpenOrdersSummaries: async (orderId) => {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_OPEN_ORDERS_SUMMARIES(orderId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  //Get activity log for an order (used in Order Details -> Activity Log tab)
  getActivityLog: async (orderId) => {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ACTIVITY_LOG(orderId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  //get order trial details for an order (used in Order Details -> Trial tab)
  getOrderTrial: async (orderId) => {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ORDER_TRIAL(orderId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get order count by status (for Regional Lead - Order by Status feature)
  getOrderByStatus: async (roleId) => {
    try {
      if (!roleId) {
        throw new Error("Role ID is required to fetch orders count by status");
      }

      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ORDERS_COUNT_BY_STATUS(roleId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );

      // Transform API response to expected format
      const apiData = response.data;
      if (apiData?.success && apiData?.data) {
        return {
          //headerText:
          // " Here is your orders count with all order status.",
          headerText: " Here is your orders count with all order status.",
          subText: "Select any status to see all orders associated to it.",
          statusData: apiData.data.map((item) => ({
            status: item.stageName,
            count: item.ordersCount,
            stageId: item.stageId,
            isMainStage: item.isMainStage,
          })),
        };
      }

      return apiData;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get order creation failures (for Regional Lead - Order Creation Pending feature)
  getCreationFailures: async () => {
    try {
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_CREATION_FAILURES,
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get orders by status ID (for Regional Lead - clicking on a status in Order by Status table)
  getOrdersByStatusId: async (roleId, stageId, isMainStage = false) => {
    try {
      if (!roleId) {
        throw new Error("Role ID is required to fetch orders by status");
      }
      if (!stageId) {
        throw new Error("Stage ID is required to fetch orders by status");
      }

      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_ORDERS_BY_STATUS_ID(roleId, stageId, isMainStage),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // GET notification details by notification ID
  getNotificationDetails: async (notificationId) => {
    try {
      if (!notificationId) {
        throw new Error(
          "Notification ID is required to fetch notification details",
        );
      }
      const response = await axiosInstance.get(
        ORDERS_ENDPOINTS.GET_NOTIFICATION_DETAILS(notificationId),
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // PATCH response feedback (thumbs up/down)
  updateResponseFeedback: async ({ threadId, turnId, feedback }) => {
    try {
      if (!threadId || !turnId) {
        throw new Error("threadId and turnId are required for feedback");
      }
      
      // Get user email from Redux store and prepend to threadId
      const state = getStore().getState();
      const userEmail = state?.user?.user?.mail || state?.user?.user?.email || state?.user?.user?.username || "";
      const prefixedThreadId = userEmail ? `${userEmail}_${threadId}` : threadId;
      
      const response = await axiosInstance.patch(
        ORDERS_ENDPOINTS.PATCHRESPONSEFEEDBACK,
        {
          threadId: prefixedThreadId,
          turnId,
          feedback,
        },
        { headers: ORDERS_TEXT_PLAIN_ACCEPT_HEADER },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default OrdersService;
